import { isDefined } from "@omni/utils/helper.utils";
import { Ref, RefCallback } from "react";

/**
 * A function that merges React refs into one.
 * Supports both functions and ref objects created using createRef() and useRef().
 *
 * Usage:
 * ```tsx
 * <div ref={mergeRefs(ref1, ref2, ref3)} />
 * ```
 *
 * @param {(React.Ref<T> | undefined)[]} inputRefs Array of refs
 * @returns {React.Ref<T> | React.RefCallback<T>} Merged refs
 */
export const mergeRefs = <T>(
    ...inputRefs: (Ref<T> | undefined)[]
): Ref<T> | RefCallback<T> => {
    const filteredInputRefs = inputRefs.filter(isDefined);

    if (filteredInputRefs.length <= 1) {
        const firstRef = filteredInputRefs[0];

        return firstRef || null;
    }

    return (ref) => {
        filteredInputRefs.forEach((inputRef) => {
            if (typeof inputRef === "function") {
                inputRef(ref);
            } else if (inputRef) {
                inputRef.current = ref;
            }
        });
    };
};
